import React, {FormEvent} from 'react';
import {Box, Typography} from '@material-ui/core';
import {CallMade} from '@material-ui/icons';
import type {FormikErrors} from 'formik';
import {useFormik} from 'formik';
import {BottomContent, MainContent} from 'layouts/commons';
import {EfeButtonPrimary, EfeFieldText, EfeLinkButton} from 'components';
import {Validations} from 'commons';
import {Messages} from 'commons/messages';
import {FlexBoxClasses} from 'assets/tss/commons/Flex';
import {useAlert, useSpinner} from 'hooks';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import Security from 'services/security/Security';
import {SnackbarVariants} from 'commons/constants';
import {useSnackbar} from 'notistack';
import {getRequiredMessage} from 'commons/utils/Messagefunctions';

/**
 * Recover credentials
 */
type RecoverCredentials = {
    username: string;
};

/**
 * Recover Password Page
 * @constructor
 */
function RecoverPassword() {
    const {enqueueSnackbar} = useSnackbar();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [requested, setRequested] = React.useState<boolean>(false);

    const formik = useFormik<RecoverCredentials>({
        initialValues: {
            username: '',
        },
        initialErrors: {
            username: '',
        },
        validate: (formValues) => {
            const required = Validations.required(['username'], formValues);
            const errors: FormikErrors<RecoverCredentials> = {
                ...required,
            };
            if (!formValues.username) {
                errors.username = getRequiredMessage('Usuario');
            }
            return errors;
        },
        validateOnBlur: true,
        onSubmit: (values, actions) => {
            actions.setSubmitting(false);
        },
    });

    /**
     * On Submit Event. Request password reset
     * @param event
     */
    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        formik.handleSubmit(event);
        setIsLoading(true);
        try {
            const response = await Security.recoverCredentials(formik.values.username);
            if (response.data) {
                enqueueSnackbar(
                    response.data.message || Messages.getAppMessage('passwordSuccess'),
                    {
                        variant: SnackbarVariants.SUCCESS,
                    }
                );
                setRequested(true);
            } else {
                enqueueSnackbar(Messages.getAppMessage('passwordError'), {
                    variant: SnackbarVariants.ERROR,
                });
            }
            setIsLoading(false);
        } catch (error: any) {
            setIsLoading(false);
            showAlertError(error, Messages.getAppMessage('passwordError'));
        }
    }

    /**
     * Header Information
     */
    const HeadInfo = (
        <Typography variant='body1' color='textPrimary'>
            {Messages.getAppMessage('password')}
        </Typography>
    );

    /**
     * Header Information for requested recover
     */
    const HeadInfoOk = (
        <Typography variant='body1' color='textPrimary'>
            {Messages.getAppMessage('requestedPassword')}
        </Typography>
    );

    /**
     * Bottom content, dynamic for requested input
     */
    const bottom = requested ? (
        <EfeLinkButton primary path={'/admin/login'} endIcon={<CallMade />}>
            {'Iniciar sesi\u00f3n'}
        </EfeLinkButton>
    ) : (
        <EfeButtonPrimary
            disabled={Object.keys(formik.errors).length != 0}
            endIcon={<CallMade />}
            type='submit'>
            {'Enviar'}
        </EfeButtonPrimary>
    );

    return (
        <form onSubmit={handleSubmit}>
            <MainContent>
                <Box flexGrow={0}>{requested ? HeadInfoOk : HeadInfo}</Box>
                {isLoading && useSpinner(isLoading)}
                {showAlert && (
                    <EfeModalMsg
                        error={genericError}
                        open={showAlert}
                        defaultError={defaultError}
                        handleAction={closeAlert}
                    />
                )}
                {!requested && (
                    <Box sx={FlexBoxClasses}>
                        <Box>
                            <EfeFieldText
                                fullWidth
                                id='username'
                                name='username'
                                label='Usuario'
                                placeholder='Ej. VECJ880326AH1'
                                value={formik.values.username}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={!!formik.errors.username}
                                helperText={formik.errors.username}
                                inputProps={{
                                    style: {
                                        textTransform: 'uppercase',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </MainContent>
            <BottomContent>{bottom}</BottomContent>
        </form>
    );
}

export default RecoverPassword;
